import React, { type ReactNode } from "react";

import { DOMNode, domToReact, Element as DomElement } from "html-react-parser";

import { renderStyledElement } from "./render-styled-element";
import { getCaptionRole } from "../utils/html-tag-styles";

/**
 * figure 자식 중 캡션 역할(figure-type/figure-description 등) 요소에만 스타일을 적용한다.
 *
 * figure 자식 전체를 상위 transform 으로 다시 흘리면 재귀 위험이 있고 기존 렌더 결과도
 * 바뀌므로, 배지·설명 스타일이 꼭 필요한 캡션 요소만 골라 renderStyledElement 로 보낸다.
 */
const transformFigureCaption = (
  reactNode: ReactNode,
  domNode: DOMNode,
  nodeIndex: number,
): React.ReactElement | null | undefined => {
  const element = domNode as DomElement;

  if ((element as any).type !== "tag") {
    return reactNode as React.ReactElement;
  }

  if (!getCaptionRole(element.name, element.attribs?.class)) {
    return reactNode as React.ReactElement;
  }

  return renderStyledElement(element, nodeIndex, transformFigureCaption);
};

/**
 * figure 태그를 렌더링 (이미지 처리 포함)
 * base64_encoding이 있으면 사용하고, 없으면 일반 img src 사용
 */
export function renderFigure(
  element: DomElement,
  base64_encoding: string,
): React.ReactElement | null {
  const className = ["flex", "flex-col", "my-4"];
  const img = element.children?.find(
    child => child.type === "tag" && (child as DomElement).name === "img",
  ) as DomElement | undefined;

  if (!img) {
    // 캡션 역할 요소만 스타일을 적용하고, 나머지는 기본 렌더링을 유지한다
    return (
      <React.Fragment>
        {domToReact((element.children as DOMNode[]) || [], {
          transform: transformFigureCaption,
        })}
      </React.Fragment>
    );
  }

  const { alt } = element.attribs || {};
  const imageSrc = base64_encoding
    ? `data:image/jpg;base64,${base64_encoding}`
    : img.attribs?.src;

  return (
    <figure className={className.join(" ")}>
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt || ""}
          loading="lazy"
          decoding="async"
          className="h-auto w-auto max-w-full self-start"
        />
      )}
      {
        // 캡션 역할 요소만 스타일을 적용하고, 나머지는 기본 렌더링을 유지한다
        domToReact(
          (element.children?.filter(
            child =>
              !(child.type === "tag" && (child as DomElement).name === "img"),
          ) as DOMNode[]) || [],
          { transform: transformFigureCaption },
        ) as React.ReactElement
      }
    </figure>
  );
}
